import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiGet, apiPost, apiPut, apiDelete, apiUpload } from '../../utils/api';
import RatesTimelineChart from './RatesTimelineChart';
import { useUrlState } from '../../hooks/useUrlState';
import './PayrollRatesManager.css';

// 관리자 메뉴 › 급여 기준값.
// 탭: 근로소득 간이세액표 / 주민세 / 고용보험 / 건강·요양보험 / 국민연금 / 비과세.
// 맨 위에는 모든 묶음의 연혁을 가로축 = 시간으로 그린 그림이 있고, 덩어리를 누르면 아래 표에서 그 시행분이 열린다.
// 탭마다 '시행월 → 값' 이력이 한 줄씩 나오고, 줄을 누르면 메모·수정 이력·수정/삭제가 펼쳐진다.
// 비과세 탭은 유형이 한 줄씩이고, 누르면 그 유형의 시행 이력이 펼쳐진다.

const API = '/api/payroll/rates';
const TAX_FREE = 'tax_free:';
const TAX_FREE_TAB = '__tax_free__';
const SIMPLE_TAX = 'simple_tax';
const TAX_FREE_FIELDS = [{ field: 'limit', label: '월 한도', unit: 'won' }];
const thisMonth = () => new Date().toISOString().slice(0, 7);
// 0.03595 → "3.595" (부동소수 꼬리 제거)
const toPercent = (v) => String(Math.round(Number(v) * 1e8) / 1e6);
const stamp = (s) => (s ? `${String(s.at).slice(0, 10)} ${s.by}` : '');
const won = (v) => Number(v).toLocaleString('ko-KR');

const formatField = (unit, value) => {
  if (value == null) return '값 없음';
  return unit === 'percent' ? `${toPercent(value)}%` : `${won(value)}원`;
};
const formatTable = (value) => {
  if (value == null) return '값 없음';
  const child = value.child ? `자녀 차감 ${value.child.map(won).join(' / ')}원` : '자녀 차감 없음';
  return `${value.row_count}구간 · ${child}`;
};
const toInput = (unit, value) => (value == null ? '' : unit === 'percent' ? toPercent(value) : String(value));
const fromInput = (unit, text) => (unit === 'percent' ? Number(text) / 100 : Number(text));
// 묶음의 값을 한 줄 글로: "요율 4.75% · 상한 6,590,000원 · 하한 410,000원"
const summarize = (group, value) => (group.item === SIMPLE_TAX
  ? formatTable(value)
  : group.fields.map((f) => `${group.fields.length > 1 ? `${f.label} ` : ''}${formatField(f.unit, value[f.field])}`).join(' · '));

function EntryForm({ group, initial, onSave, onCancel, isNew }) {
  const isTable = group.item === SIMPLE_TAX;
  const [month, setMonth] = useState(initial.effective_from);
  const [note, setNote] = useState(initial.note || '');
  const [error, setError] = useState('');
  // 칸별 입력 문자열과 '값 없음' 여부
  const [texts, setTexts] = useState(() => Object.fromEntries(
    group.fields.map((f) => [f.field, toInput(f.unit, initial.value ? initial.value[f.field] : null)])));
  const [none, setNone] = useState(() => Object.fromEntries(
    group.fields.map((f) => [f.field, !isNew && (!initial.value || initial.value[f.field] == null)])));
  // 간이세액표 전용
  const [tableNone, setTableNone] = useState(isTable && !isNew && initial.value == null);
  const [child, setChild] = useState(isTable && initial.value && initial.value.child ? initial.value.child.map(String) : ['', '', '']);
  const [file, setFile] = useState(null);

  const submit = () => {
    setError('');
    if (!month) { setError('시행월을 넣어주세요'); return; }
    if (isTable) {
      if (tableNone) { onSave({ effective_from: month, value: null, note }); return; }
      if (isNew) {
        if (!file) { setError('간이세액표 엑셀 파일을 선택하세요'); return; }
        onSave({ effective_from: month, note, file });
        return;
      }
      const filled = child.every((c) => c !== '');
      if (!filled && child.some((c) => c !== '')) { setError('자녀 차감액은 세 칸을 모두 넣거나 모두 비우세요'); return; }
      onSave({ effective_from: month, note, value: { child: filled ? child.map(Number) : null } });
      return;
    }
    const value = {};
    for (const f of group.fields) {
      if (none[f.field]) { value[f.field] = null; continue; }
      const text = texts[f.field];
      if (text === '' || Number.isNaN(Number(text))) { setError(`${f.label}: 숫자를 넣거나 "값 없음"을 선택하세요`); return; }
      value[f.field] = fromInput(f.unit, text);
    }
    onSave({ effective_from: month, value, note });
  };

  return (
    <div className="prm-form" onClick={(e) => e.stopPropagation()}>
      <div className="prm-form-row">
        <label>시행월<input type="month" value={month} onChange={(e) => setMonth(e.target.value)} /></label>
        <label className="prm-grow">메모<input value={note} onChange={(e) => setNote(e.target.value)} placeholder="근거·출처" /></label>
      </div>
      {!isTable && (
        <div className="prm-fields">
          {group.fields.map((f) => (
            <div className="prm-field" key={f.field}>
              <label>
                {f.label} {f.unit === 'percent' ? '(%)' : '(원)'}
                <input
                  type="number"
                  step="any"
                  value={none[f.field] ? '' : texts[f.field]}
                  disabled={none[f.field]}
                  placeholder={none[f.field] ? '값 없음' : ''}
                  onChange={(e) => setTexts({ ...texts, [f.field]: e.target.value })}
                />
              </label>
              <label className="prm-check" title="그 시기에 제도가 없었음을 기록합니다. 요율이면 공제하지 않고, 상·하한이면 제한하지 않고, 비과세면 과세로 봅니다.">
                <input type="checkbox" checked={none[f.field]} onChange={(e) => setNone({ ...none, [f.field]: e.target.checked })} />
                값 없음
              </label>
            </div>
          ))}
        </div>
      )}
      {isTable && (
        <div className="prm-form-row">
          <label className="prm-check" title="이 시기에는 간이세액표를 적용하지 않습니다(갑근세 0원).">
            <input type="checkbox" checked={tableNone} onChange={(e) => setTableNone(e.target.checked)} />
            값 없음
          </label>
          {!tableNone && isNew && (
            <>
              <input type="file" accept=".xlsx" onChange={(e) => setFile(e.target.files[0] || null)} />
              <span className="prm-muted">국세청 「근로소득 간이세액표」 엑셀을 그대로 올리면 세액표와 8~20세 자녀 차감액을 함께 읽습니다.</span>
            </>
          )}
          {!tableNone && !isNew && ['자녀 1명 차감(원)', '자녀 2명 차감(원)', '2명 초과 1명당(원)'].map((label, i) => (
            <label key={label}>{label}<input type="number" value={child[i]} onChange={(e) => setChild(child.map((c, k) => (k === i ? e.target.value : c)))} /></label>
          ))}
          {!tableNone && !isNew && <span className="prm-muted">세액표 본문은 새 시행월로 엑셀을 올려 교체합니다.</span>}
        </div>
      )}
      <div className="prm-form-row end">
        {error && <span className="prm-form-error">{error}</span>}
        <button type="button" className="btn btn-secondary btn-sm" onClick={onCancel}>취소</button>
        <button type="button" className="btn btn-primary btn-sm" onClick={submit}>저장</button>
      </div>
    </div>
  );
}

// 한 묶음(또는 비과세 유형 하나)의 시행 이력. 한 줄에 이력 하나, 누르면 상세가 펼쳐진다.
function Timeline({ group, month, run, focus }) {
  const isTable = group.item === SIMPLE_TAX;
  const [open, setOpen] = useState(null);       // 펼친 이력 id
  // 연혁 그림에서 고른 시행분을 연다
  const focusId = focus && focus.item === group.item ? focus.id : null;
  const focusNonce = focus ? focus.nonce : null;
  useEffect(() => { if (focusId) setOpen(focusId); }, [focusId, focusNonce]);
  const [editing, setEditing] = useState(null); // 'new' | 이력 id
  const span = (isTable ? 1 : group.fields.length) + 2;

  const save = (entry, form) => {
    if (form.file) {
      const fd = new FormData();
      fd.append('file', form.file);
      fd.append('effective_from', form.effective_from);
      fd.append('note', form.note || '');
      return run(() => apiUpload(`${API}/simple-tax/upload`, fd),
        (r) => (r.child_found ? '간이세액표를 등록했습니다' : '간이세액표를 등록했습니다. 자녀 차감액을 찾지 못했으니 수정에서 직접 넣어주세요'))
        .then((ok) => ok && setEditing(null));
    }
    const body = { item: group.item, effective_from: form.effective_from, value: form.value, note: form.note };
    return run(() => (entry ? apiPut(`${API}/${entry.id}`, body) : apiPost(API, body)), '저장했습니다')
      .then((ok) => ok && setEditing(null));
  };

  const remove = (entry) => {
    if (!window.confirm(`${group.section ? `${group.section} · ` : ''}${group.label}의 ${entry.effective_from} 시행 이력을 삭제할까요?`)) return;
    run(() => apiDelete(`${API}/${entry.id}`), '삭제했습니다');
  };

  return (
    <div className="prm-timeline">
      <div className="prm-timeline-head">
        <span className="prm-muted">
          시행월부터 다음 이력 전까지 적용됩니다.
          {group.fields.length > 1 && ' 요율과 상·하한은 시행월을 함께 쓰므로 하나만 바뀌어도 새 이력을 추가합니다(나머지는 직전 값이 채워져 있습니다).'}
          {' '}줄을 누르면 메모와 수정 이력이 열립니다.
        </span>
        <button type="button" className="btn btn-primary btn-sm" onClick={() => setEditing('new')}>
          {isTable ? '+ 새 시행분 업로드' : '+ 시행 이력 추가'}
        </button>
      </div>

      {editing === 'new' && (
        <EntryForm
          key={`new-${group.item}`}
          isNew
          group={group}
          // 새 이력은 직전 값에서 시작한다(바뀐 칸만 고치면 되게)
          initial={{ effective_from: month, value: !isTable && group.entries[0] ? group.entries[0].value : null, note: '' }}
          onCancel={() => setEditing(null)}
          onSave={(form) => save(null, form)}
        />
      )}

      {group.entries.length === 0 ? (
        <div className="prm-empty">등록된 이력이 없습니다. 이 기간의 임금대장은 열 수 없습니다.</div>
      ) : (
        <table className="prm-table">
          <thead>
            <tr>
              <th style={{ width: 150 }}>시행월</th>
              {isTable ? <th>세액표</th> : group.fields.map((f) => <th key={f.field} className="num">{f.label}</th>)}
              <th style={{ width: 28 }} />
            </tr>
          </thead>
          <tbody>
            {group.entries.map((entry, index) => {
              const older = group.entries[index + 1];
              const isCurrent = group.current.id === entry.id;
              const isOpen = open === entry.id || editing === entry.id;
              return (
                <React.Fragment key={entry.id}>
                  <tr className={`prm-row ${isCurrent ? 'current' : ''} ${isOpen ? 'open' : ''}`} onClick={() => setOpen(isOpen ? null : entry.id)}>
                    <td className="prm-mono">
                      {entry.effective_from}
                      {isCurrent && <span className="prm-chip ok">{month} 적용</span>}
                    </td>
                    {isTable
                      ? <td className={entry.value == null ? 'prm-novalue' : 'prm-value'}>{formatTable(entry.value)}</td>
                      : group.fields.map((f) => {
                        const v = entry.value[f.field];
                        // 직전 이력과 달라진 칸을 굵게: 어느 값이 이 시행월에 바뀌었는지 보이게
                        const changed = !older || older.value[f.field] !== v;
                        return <td key={f.field} className={`num ${v == null ? 'prm-novalue' : changed ? 'prm-value' : 'prm-same'}`}>{formatField(f.unit, v)}</td>;
                      })}
                    <td className="prm-caret">{isOpen ? '▾' : '▸'}</td>
                  </tr>
                  {isOpen && (
                    <tr className="prm-detail-row">
                      <td colSpan={span}>
                        {editing === entry.id ? (
                          <EntryForm key={entry.id} group={group} initial={entry} onCancel={() => setEditing(null)} onSave={(form) => save(entry, form)} />
                        ) : (
                          <div className="prm-detail">
                            <dl>
                              <dt>메모</dt><dd>{entry.note || <span className="prm-muted">없음</span>}</dd>
                              {isTable && entry.value && entry.value.source && (<><dt>원본 파일</dt><dd>{entry.value.source}</dd></>)}
                              <dt>최종 수정</dt><dd>{stamp(entry.updated)}</dd>
                              {entry.history.length > 0 && (
                                <>
                                  <dt>수정 이력</dt>
                                  <dd>
                                    {[...entry.history].reverse().map((h, i) => (
                                      <div key={`h-${i}`}>
                                        <span className="prm-muted">{stamp(h)} 수정 전</span> · 시행월 {h.effective_from} · {isTable
                                          ? formatTable(h.value)
                                          : group.fields.map((f) => `${f.label} ${formatField(f.unit, h.value ? h.value[f.field] : null)}`).join(', ')}
                                        {h.note ? ` · ${h.note}` : ''}
                                      </div>
                                    ))}
                                  </dd>
                                </>
                              )}
                            </dl>
                            <div className="prm-detail-actions">
                              <button type="button" className="btn btn-secondary btn-sm" onClick={(e) => { e.stopPropagation(); setEditing(entry.id); }}>수정</button>
                              <button type="button" className="btn btn-danger btn-sm" onClick={(e) => { e.stopPropagation(); remove(entry); }}>삭제</button>
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function PayrollRatesManager() {
  // 기준 월과 고른 묶음은 주소에 둔다(?tab=payroll-rates&rate_month=2026-10&rate=health)
  const [url, setUrl] = useUrlState();
  const month = url.get('rate_month') || thisMonth();
  const setMonth = (value) => setUrl({ rate_month: value === thisMonth() ? null : value }, { replace: true });
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const tab = url.get('rate') || SIMPLE_TAX;
  const setTab = (value) => setUrl({ rate: value === SIMPLE_TAX ? null : value });
  const [openType, setOpenType] = useState(null);   // 비과세 탭에서 펼친 유형
  const [newType, setNewType] = useState('');
  const [draftTypes, setDraftTypes] = useState([]); // 아직 이력이 없는 새 비과세 유형
  const [focus, setFocus] = useState(null);         // 연혁 그림에서 고른 시행분 {item, id, nonce}

  const load = useCallback(async () => {
    try {
      setData(await apiGet(`${API}?month=${month}`));
      setError('');
    } catch (e) {
      setError(e.message);
    }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  // 성공하면 true. 실패하면 오류를 띄우고 false (폼은 열어 둔다)
  const run = useCallback(async (fn, done) => {
    setError('');
    setMessage('');
    try {
      const result = await fn();
      setMessage(typeof done === 'function' ? done(result) : done);
      await load();
      return true;
    } catch (e) {
      setError(e.message);
      return false;
    }
  }, [load]);

  const { mainGroups, taxFreeGroups } = useMemo(() => {
    if (!data) return { mainGroups: [], taxFreeGroups: [] };
    const main = data.groups.filter((g) => !g.section);
    const free = data.groups.filter((g) => g.section);
    const have = new Set(free.map((g) => g.item));
    const drafts = draftTypes.filter((name) => !have.has(TAX_FREE + name)).map((name) => ({
      item: TAX_FREE + name, label: name, section: data.tax_free_label, fields: TAX_FREE_FIELDS, entries: [], current: { status: 'missing' },
    }));
    return { mainGroups: main, taxFreeGroups: [...free, ...drafts] };
  }, [data, draftTypes]);

  if (!data) return <div className="prm">{error ? <div className="prm-error">{error}</div> : '불러오는 중...'}</div>;

  const missing = mainGroups.filter((g) => g.current.status === 'missing' && !g.optional);
  const group = mainGroups.find((g) => g.item === tab);
  const needsSeed = data.groups.some((g) => g.entries.length === 0);

  const addType = () => {
    const name = newType.trim();
    if (!name) return;
    setDraftTypes((d) => (d.includes(name) ? d : [...d, name]));
    setNewType('');
    setOpenType(TAX_FREE + name);
  };

  return (
    <div className="prm">
      <div className="prm-top">
        <label className="prm-month">
          기준 월
          <input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} />
        </label>
        <span className="prm-top-text">
          {month} 임금대장에 적용되는 값을 표시합니다.
          {missing.length > 0
            ? <b className="prm-warn"> 미등록: {missing.map((g) => g.label).join(', ')} — 이 달 임금대장은 열리지 않습니다.</b>
            : <b className="prm-good"> 모두 등록되어 있습니다.</b>}
        </span>
        {needsSeed && (
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            title="이력이 한 건도 없는 묶음에만 기본 연혁을 다시 넣습니다. 관리 중인 묶음은 건드리지 않습니다."
            onClick={() => run(() => apiPost(`${API}/seed-defaults`, {}), (r) => (r.created.length ? `기본 연혁을 채웠습니다: ${r.created.map((c) => c.label).join(', ')}` : '채울 묶음이 없습니다'))}
          >
            빈 묶음에 기본 연혁 채우기
          </button>
        )}
      </div>
      {error && <div className="prm-error">{error}</div>}
      {message && <div className="prm-message">{message}</div>}

      <RatesTimelineChart
        groups={[...mainGroups, ...taxFreeGroups.filter((g) => g.entries.length)]}
        month={month}
        selected={focus ? focus.id : null}
        onPick={(g, id) => {
          if (g.section) { setTab(TAX_FREE_TAB); setOpenType(g.item); } else setTab(g.item);
          setFocus({ item: g.item, id, nonce: Date.now() });
        }}
      />

      <div className="prm-tabs">
        {mainGroups.map((g) => (
          <button type="button" key={g.item} className={`prm-tab ${tab === g.item ? 'on' : ''}`} onClick={() => setTab(g.item)}>
            {g.label}
            {g.current.status === 'missing' && !g.optional && <span className="prm-dot" title={`${month}에 적용할 이력 없음`} />}
          </button>
        ))}
        <button type="button" className={`prm-tab ${tab === TAX_FREE_TAB ? 'on' : ''}`} onClick={() => setTab(TAX_FREE_TAB)}>
          {data.tax_free_label}
        </button>
      </div>

      <div className="prm-panel">
        {group && (
          <>
            <div className={`prm-current ${group.current.status}`}>
              <b>{month} 적용</b>
              {group.current.status === 'missing'
                ? <span>이 달에 적용할 이력이 없습니다 (미등록)</span>
                : <span>{summarize(group, group.current.value)} <span className="prm-muted">· {group.current.effective_from} 시행분</span></span>}
            </div>
            <Timeline key={group.item} group={group} month={month} run={run} focus={focus} />
          </>
        )}

        {tab === TAX_FREE_TAB && (
          <>
            <div className="prm-timeline-head">
              <span className="prm-muted">비과세 유형마다 한도 이력을 따로 관리합니다. 줄을 누르면 그 유형의 시행 이력이 열립니다. 임금대장 설정에서 수당을 비과세로 지정할 때 이 유형 중에서 고릅니다.</span>
              <span className="prm-add-type">
                <input value={newType} placeholder="새 비과세 유형 이름" onChange={(e) => setNewType(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addType()} />
                <button type="button" className="btn btn-primary btn-sm" onClick={addType}>+ 유형 추가</button>
              </span>
            </div>
            <table className="prm-table">
              <thead>
                <tr><th style={{ width: 200 }}>유형</th><th className="num" style={{ width: 180 }}>{month} 월 한도</th><th>적용 중인 시행분</th><th style={{ width: 90 }}>이력 수</th><th style={{ width: 28 }} /></tr>
              </thead>
              <tbody>
                {taxFreeGroups.length === 0 && <tr><td colSpan={5} className="prm-empty">등록된 비과세 유형이 없습니다.</td></tr>}
                {taxFreeGroups.map((g) => {
                  const isOpen = openType === g.item;
                  const cur = g.current;
                  return (
                    <React.Fragment key={g.item}>
                      <tr className={`prm-row ${isOpen ? 'open' : ''}`} onClick={() => setOpenType(isOpen ? null : g.item)}>
                        <td className="prm-value">{g.label}</td>
                        <td className={`num ${cur.status === 'missing' ? 'prm-missing' : cur.value.limit == null ? 'prm-novalue' : 'prm-value'}`}>
                          {cur.status === 'missing' ? '미등록' : formatField('won', cur.value.limit)}
                        </td>
                        <td className="prm-muted">{cur.effective_from ? `${cur.effective_from} 시행분` : ''}</td>
                        <td className="prm-muted">{g.entries.length}건</td>
                        <td className="prm-caret">{isOpen ? '▾' : '▸'}</td>
                      </tr>
                      {isOpen && (
                        <tr className="prm-detail-row">
                          <td colSpan={5}><Timeline key={g.item} group={g} month={month} run={run} focus={focus} /></td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
}

export default PayrollRatesManager;
